/** Display helpers. Times always show in the school's time zone, not the browser's. */

export function formatDuration(minutes: number | null): string {
  if (minutes === null) return "";
  const h = Math.floor(minutes / 60);
  const m = Math.abs(minutes % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

export function formatHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

export function formatTime(iso: string | null, timezone: string): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "2-digit" }).format(
    new Date(iso),
  );
}

export function formatDay(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

export function formatDateTime(iso: string, timezone?: string): string {
  return new Intl.DateTimeFormat("en-US", {
    ...(timezone ? { timeZone: timezone } : {}),
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** YYYY-MM-DD for "now" in a time zone. */
export function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(),
  );
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  return utc.toISOString().slice(0, 10);
}

export function weekStart(date: string, weekStartsOn: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const utc = new Date(Date.UTC(y, m - 1, d));
  return addDays(date, -((utc.getUTCDay() - weekStartsOn + 7) % 7));
}

/**
 * Value for an <input type="datetime-local"> showing an instant in a time
 * zone, and the reverse. The browser's own zone may differ from the school's.
 */
export function toLocalInput(iso: string | null, timezone: string): string {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

export function fromLocalInput(value: string, timezone: string): string | null {
  if (!value) return null;
  const asUtc = Date.parse(`${value}:00Z`);
  if (Number.isNaN(asUtc)) return null;
  // Find the offset of the school's zone at that wall-clock time, twice, so a
  // value near a daylight saving change lands on the right side of it.
  let guess = asUtc;
  for (let i = 0; i < 2; i += 1) {
    const shown = Date.parse(`${toLocalInput(new Date(guess).toISOString(), timezone)}:00Z`);
    guess += asUtc - shown;
  }
  const result = new Date(guess).toISOString();
  // A wall-clock time skipped by the spring daylight saving change (2:30 AM
  // on the second Sunday of March in New York) does not exist. Refuse it
  // rather than silently landing an hour off.
  return toLocalInput(result, timezone) === value ? result : null;
}
